import React, {useEffect, useState} from 'react';
import {AddIcon, EditLargeIcon, LoadingIcon} from "../../assets/icons/Icons";
import FormSection from "../form/Section";
import FormHeader from "../form/Header";
import Popup from "../Popup";
import CustomSelect from "../form/CustomSelect";
import ReportTypeSelect from "../form/ReportTypeSelect";
import ReportMethodSelect from "../form/ReportMethodSelect";
import {UpdateReport} from "../../api/reports/updateReport";
import CustomTooltip from "../CustomTooltip";
import {useTranslation} from "react-i18next";

const EditReportForm = ({reportSubscription, onUpdate, onClose, ...props}) => {

    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(reportSubscription.report);
    const [frequency, setFrequency] = useState(reportSubscription.frequency);
    const [method, setMethod] = useState(reportSubscription.reporting_method);
    const [email, setEmail] = useState(reportSubscription.email);
    const [timeRange, setTimeRange] = useState(reportSubscription.time_range);
    const [submitDisabled, setSubmitDisabled] = useState(true);

    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitDisabled(true);
        try {
            await UpdateReport(reportSubscription.id, email, report, frequency, timeRange, method);
            setLoading(false);
            setSubmitDisabled(false);
            onUpdate();
        } catch (err){
            setError(err.message);
            setLoading(false);
            setSubmitDisabled(false);
            console.log("Error fetching data: " + err);
        }
    }

    useEffect(() => {
        if (
            report !== reportSubscription.report ||
            frequency !== reportSubscription.frequency ||
            method !== reportSubscription.reporting_method ||
            email !== reportSubscription.email ||
            timeRange !== reportSubscription.time_range
        ) {
            setSubmitDisabled(false);
        } else {
            setSubmitDisabled(true);
        }
    }, [report,frequency,method,email,timeRange]);

    const timeRangeOptions = [
        {
            value: 'yesterday',
            label: t('Yesterday')
        },
        {
            value: 'previous_7_days',
            label: t('Previous 7 days')
        },
        {
            value: 'previous_31_days',
            label: t('Previous 31 days')
        },
        {
            value: 'previous_93_days',
            label: t('Previous 93 days')
        },
        {
            value: 'all_time',
            label: t('All time')
        }
    ]

    const frequencyOptions = [
        {
            value: '@daily',
            label: t('Daily')
        },
        {
            value: '@weekly',
            label: t('Weekly')
        },
        {
            value: '@monthly',
            label: t('Monthly')
        }
    ]

    return (
        <Popup size="large" withClose withOverlay onClose={onClose}>
            <form method="post" onSubmit={handleSubmit}>

                <FormHeader
                    title={t("Edit")}
                    description={t("Edit report here.")}
                    icon={<EditLargeIcon/>}
                ></FormHeader>
                <div className="row mt-5">
                    <div className="col-xs-6">
                        <FormSection title={t("Report type")} required>
                            <ReportTypeSelect value={report} onChange={option => setReport(option.value)} />
                        </FormSection>
                        <FormSection title={t("Time frequency")} beforeTitle={<CustomTooltip message={
                            <ul>
                                <li><strong>{t("Daily")}:</strong> {t("Every day at 6 PM UTC")}</li>
                                <li><strong>{t("Weekly")}:</strong> {t("Every Sunday at 6 PM UTC")}</li>
                                <li><strong>{t("Monthly")}:</strong> {t("Every first of the month at 6 PM UTC")}</li>
                            </ul>}/>} required>
                            <CustomSelect
                                placeholder={<div>Select Frequency</div>}
                                options={frequencyOptions}
                                onChange={option => setFrequency(option.value)}
                                value={frequencyOptions.find(option => option.value && frequency && option.value === frequency.toLowerCase())}
                            />
                        </FormSection>
                        <FormSection title={t("Reporting method")} required>
                            <ReportMethodSelect value={method} onChange={option => setMethod(option.value)} />
                        </FormSection>
                    </div>
                    <div className="col-xs-6">
                        <FormSection title={t("Email")} required>
                            <input type="email" name="email" id="email" placeholder={t("Email")} value={email}
                                   onChange={e => setEmail(e.target.value)}/>
                        </FormSection>
                        <FormSection title={t("Time range")} beforeTitle={<CustomTooltip message={
                            <ul>
                                <li>{t("You may select a time range up to 93 days in the past from today’s date.")}</li>
                            </ul>}/>} required>
                            <CustomSelect
                                placeholder={<div>{t("Select time range")}</div>}
                                options={timeRangeOptions}
                                onChange={option => setTimeRange(option.value)}
                                value={timeRangeOptions.find(option => option.value && timeRange && option.value === timeRange.toLowerCase())}
                            />
                        </FormSection>
                    </div>
                </div>
                <div className="text-right mt-5">
                    <button className={`btn btn-primary btn-medium ${loading ? 'btn-loading' : ''}`} type="submit"
                            name="submit" disabled={submitDisabled}>
                        {loading ? <LoadingIcon/> : <AddIcon/>}
                        {t("Save")}
                    </button>
                </div>
                {error && <p className="color-error text-center mt-3">{error}</p>}
            </form>
        </Popup>
    );
};

export default EditReportForm;